export interface ServiceData {
  slug: string;
  title: string;
  shortDescription: string;
  fullDescription: string;
  category: 'delivery' | 'business' | 'specialized';
  icon: string; // heroicon name
  useCases: string[];
  benefits: string[];
  whoItsFor: string[];
}

export const services: ServiceData[] = [
  // ── DELIVERY TYPES ──
  {
    slug: 'standard-delivery',
    title: 'Standard Delivery',
    shortDescription: 'Reliable next-business-day delivery across British Columbia for regular shipments.',
    fullDescription: 'Our standard delivery service provides dependable next-business-day shipping across BC. Ideal for routine shipments that don\'t require same-day urgency but still need professional handling, real-time tracking, and proof of delivery.',
    category: 'delivery',
    icon: 'truck',
    useCases: [
      'Routine business-to-business shipments',
      'Office supply and document deliveries',
      'Non-urgent customer orders',
      'Scheduled inventory transfers between locations',
      'Regular vendor and supplier deliveries',
    ],
    benefits: [
      'Most affordable delivery option',
      'Real-time GPS tracking on every shipment',
      'Digital proof of delivery with signature',
      'Coverage across all major BC cities',
      'Reliable next-business-day service window',
    ],
    whoItsFor: ['Offices', 'Retail Stores', 'Small Businesses', 'Warehouses'],
  },
  {
    slug: 'rush-delivery',
    title: 'Rush Delivery',
    shortDescription: 'Priority same-day delivery within 3-4 hours for time-sensitive packages.',
    fullDescription: 'When standard timing isn\'t fast enough, our rush delivery service gets your package there within 3-4 hours. Priority dispatch, optimized routing, and dedicated couriers ensure your urgent shipments arrive on time across the Lower Mainland and beyond.',
    category: 'delivery',
    icon: 'bolt',
    useCases: [
      'Urgent client deliveries with tight deadlines',
      'Time-sensitive legal documents and contracts',
      'Same-day parts and supplies for operations',
      'Last-minute order fulfillment',
      'Emergency restocking for retail locations',
    ],
    benefits: [
      'Delivery within 3-4 hours of pickup',
      'Priority dispatch — your order goes first',
      'Live tracking with accurate ETA updates',
      'Signature confirmation on delivery',
      'Available Monday through Saturday',
    ],
    whoItsFor: ['Law Firms', 'Medical Clinics', 'E-Commerce', 'Corporate Offices'],
  },
  {
    slug: 'direct-delivery',
    title: 'Direct Delivery',
    shortDescription: 'Non-stop, point-to-point transport with no transfers or stops.',
    fullDescription: 'Direct delivery means your package goes straight from pickup to destination with zero transfers, no shared routes, and no unnecessary stops. This is the fastest and most secure option for high-priority shipments that demand exclusive handling.',
    category: 'delivery',
    icon: 'arrow-right',
    useCases: [
      'High-value or confidential shipments',
      'Executive and VIP courier requests',
      'Fragile items requiring careful handling',
      'Urgent medical specimens and lab samples',
      'Time-critical legal filings and court documents',
    ],
    benefits: [
      'Fastest delivery option — typically 1-3 hours',
      'Dedicated driver — no shared routes',
      'No transfers or intermediate handling',
      'Chain-of-custody tracking for sensitive items',
      'Available for after-hours and weekend emergencies',
    ],
    whoItsFor: ['Healthcare', 'Legal', 'Finance', 'Executive Services'],
  },
  {
    slug: 'pallet-delivery',
    title: 'Pallet Delivery',
    shortDescription: 'Freight and pallet shipping for bulk, oversized, and heavy shipments.',
    fullDescription: 'For shipments that go beyond standard parcel sizes, our pallet delivery service handles bulk freight, oversized items, and heavy cargo across British Columbia. Liftgate service, freight-grade handling, and flexible scheduling for your large-scale logistics needs.',
    category: 'delivery',
    icon: 'cube',
    useCases: [
      'Warehouse-to-warehouse inventory transfers',
      'Bulk retail product distribution',
      'Construction materials and industrial parts',
      'Trade show and event equipment',
      'Large-format printing and signage delivery',
    ],
    benefits: [
      'Handles pallets up to 48" x 48" x 48"',
      'Liftgate service included',
      'Freight-grade securing and handling',
      'Next-day or same-day pallet options',
      'Competitive pricing for bulk shipments',
    ],
    whoItsFor: ['Warehouses', 'Construction', 'Manufacturing', 'Retail Chains'],
  },
  {
    slug: 'scheduled-delivery',
    title: 'Scheduled Delivery',
    shortDescription: 'Recurring daily or weekly pickup routes tailored to your business.',
    fullDescription: 'Set up recurring pickup and delivery schedules that fit your business operations. Whether you need daily courier runs, weekly inventory transfers, or custom routes — scheduled delivery gives you consistency, predictability, and reduced per-shipment costs.',
    category: 'delivery',
    icon: 'calendar',
    useCases: [
      'Daily inter-office mail and document runs',
      'Weekly inventory replenishment routes',
      'Regular pharmacy and lab sample pickups',
      'Scheduled e-commerce fulfillment batches',
      'Recurring supplier and vendor deliveries',
    ],
    benefits: [
      'Consistent pickup times you can rely on',
      'Lower per-shipment cost with volume commitment',
      'Dedicated route driver who knows your business',
      'Flexible scheduling — daily, weekly, or custom',
      'Automatic volume discount eligibility',
    ],
    whoItsFor: ['Pharmacies', 'Offices', 'Clinics', 'Retail Chains'],
  },

  // ── BUSINESS SOLUTIONS ──
  {
    slug: 'last-mile-delivery',
    title: 'Last-Mile Delivery',
    shortDescription: 'The final leg of delivery — from distribution hub to your customer\'s door.',
    fullDescription: 'Last-mile delivery is the most critical part of the shipping process. SpeedyShip specializes in getting packages from your warehouse or distribution center to the end customer quickly, reliably, and with full visibility. We handle the complexity so your customers get a premium delivery experience.',
    category: 'business',
    icon: 'map-pin',
    useCases: [
      'E-commerce order fulfillment to end customers',
      'Subscription box and meal kit deliveries',
      'Furniture and large-item home delivery',
      'Grocery and pharmacy doorstep delivery',
      'Multi-drop residential distribution routes',
    ],
    benefits: [
      'Reliable delivery windows for customer satisfaction',
      'Real-time tracking shared with your customers',
      'Proof of delivery with photo and signature',
      'Failed delivery attempt management',
      'Scalable capacity for peak seasons',
    ],
    whoItsFor: ['E-Commerce', 'Subscription Services', 'Grocery', 'Retail'],
  },
  {
    slug: 'ecommerce-delivery',
    title: 'E-Commerce Delivery',
    shortDescription: 'Same-day and next-day fulfillment for online stores across BC.',
    fullDescription: 'Give your online customers the delivery speed they expect. SpeedyShip integrates with your e-commerce workflow to provide same-day and next-day delivery across British Columbia. Faster delivery means fewer cart abandonments, better reviews, and more repeat customers.',
    category: 'business',
    icon: 'shopping-bag',
    useCases: [
      'Same-day delivery for local online orders',
      'Next-day fulfillment across BC',
      'Multi-item order consolidation',
      'Return pickup and processing',
      'Holiday and peak season capacity',
    ],
    benefits: [
      'Same-day delivery option for competitive advantage',
      'Branded tracking page for your customers',
      'Flexible pickup from your warehouse or store',
      'Volume discounts that scale with your business',
      'Dedicated account support for e-commerce clients',
    ],
    whoItsFor: ['Online Stores', 'Shopify Merchants', 'DTC Brands', 'Marketplaces'],
  },
  {
    slug: 'business-delivery',
    title: 'Business Delivery',
    shortDescription: 'Dedicated courier service for B2B operations and corporate logistics.',
    fullDescription: 'SpeedyShip\'s business delivery service is built for companies that need reliable, professional courier support as part of their daily operations. From inter-office runs to client deliveries, we provide the infrastructure so your business runs smoothly.',
    category: 'business',
    icon: 'building-office',
    useCases: [
      'Client document and contract deliveries',
      'Inter-office mail and package runs',
      'Vendor and supplier pickup coordination',
      'Sample and prototype distribution',
      'Corporate gift and event material delivery',
    ],
    benefits: [
      'Business account with volume discounts',
      'Saved addresses for repeat deliveries',
      'Monthly invoicing for approved accounts',
      'Dedicated account manager',
      'Portal access for team ordering',
    ],
    whoItsFor: ['Corporate Offices', 'Law Firms', 'Consulting', 'Agencies'],
  },
  {
    slug: 'bulk-delivery',
    title: 'Bulk Delivery',
    shortDescription: 'High-volume delivery for businesses shipping 10+ packages daily.',
    fullDescription: 'When you\'re shipping at scale, every dollar and minute counts. Our bulk delivery program is designed for businesses moving high volumes daily — with tiered pricing, batch processing, and dedicated capacity to handle your throughput without bottlenecks.',
    category: 'business',
    icon: 'squares',
    useCases: [
      'Daily batch shipments from warehouses',
      'Multi-location retail distribution',
      'Promotional campaign and flyer distribution',
      'Product launch and rollout deliveries',
      'Seasonal inventory redistribution',
    ],
    benefits: [
      'Volume pricing — up to 20% off at 50+ orders/month',
      'Batch pickup scheduling for efficiency',
      'Dedicated fleet capacity during peak periods',
      'Simplified invoicing for high-volume accounts',
      'Priority dispatch on all bulk orders',
    ],
    whoItsFor: ['Distributors', 'Retailers', 'Fulfillment Centers', 'Marketing Firms'],
  },
  {
    slug: 'warehouse-distribution',
    title: 'Warehouse Distribution',
    shortDescription: 'Efficient distribution from your warehouse to multiple destinations.',
    fullDescription: 'Connect your warehouse operations to SpeedyShip\'s delivery network. We handle multi-drop distribution, inventory transfers between facilities, and last-mile delivery from your storage locations — all with tracking, proof of delivery, and flexible scheduling.',
    category: 'business',
    icon: 'warehouse',
    useCases: [
      'Multi-drop distribution from central warehouse',
      'Cross-dock and transfer between facilities',
      'Retail store replenishment from distribution center',
      'Overflow storage and delivery coordination',
      'Just-in-time inventory delivery to production',
    ],
    benefits: [
      'Multi-stop route optimization',
      'Integration with your warehouse schedule',
      'Real-time visibility on all outbound shipments',
      'Pallet and parcel handling in one service',
      'Scalable capacity that grows with your volume',
    ],
    whoItsFor: ['Warehouses', '3PL Providers', 'Distributors', 'Manufacturers'],
  },

  // ── SPECIALIZED SERVICES ──
  {
    slug: 'same-day-delivery',
    title: 'Same-Day Delivery',
    shortDescription: 'Guaranteed delivery before end of business day across BC.',
    fullDescription: 'Book before the cutoff time and your package is delivered the same day. SpeedyShip\'s same-day service is perfect for businesses that need guaranteed delivery within business hours — no overnight waits, no next-day delays.',
    category: 'specialized',
    icon: 'clock',
    useCases: [
      'Urgent documents that can\'t wait until tomorrow',
      'Same-day customer order fulfillment',
      'Emergency supply and parts delivery',
      'Last-minute event and presentation materials',
      'Critical healthcare shipments within BC',
    ],
    benefits: [
      'Delivered before 5 PM same business day',
      'Book as late as 2 PM for same-day (rush service)',
      'Real-time tracking from pickup to delivery',
      'Digital proof of delivery instantly',
      'Coverage across the Lower Mainland and BC cities',
    ],
    whoItsFor: ['Healthcare', 'Legal', 'Corporate', 'E-Commerce'],
  },
  {
    slug: 'express-delivery',
    title: 'Express Delivery',
    shortDescription: 'Fastest possible delivery — dedicated courier, direct route, no delays.',
    fullDescription: 'Express delivery is for when nothing else is fast enough. A dedicated courier picks up your package immediately and takes the most direct route to the destination. No shared routes, no batch processing — just speed.',
    category: 'specialized',
    icon: 'lightning',
    useCases: [
      'Emergency medical and lab deliveries',
      'Court filing deadline submissions',
      'Time-critical financial documents',
      'Executive-level urgent courier requests',
      'After-hours and weekend urgent deliveries',
    ],
    benefits: [
      'Pickup within 30 minutes of booking',
      'Dedicated courier — no shared routes',
      'Fastest transit time available',
      'Available 24/7 including weekends',
      'Real-time ETA updates during transit',
    ],
    whoItsFor: ['Hospitals', 'Law Firms', 'Banks', 'Executives'],
  },
  {
    slug: 'medical-delivery',
    title: 'Medical Delivery',
    shortDescription: 'Secure, temperature-controlled transport for healthcare and lab shipments.',
    fullDescription: 'SpeedyShip\'s medical delivery service is designed for the healthcare industry. We transport lab samples, pharmaceuticals, medical devices, and sensitive biological materials with temperature-controlled vehicles, chain-of-custody protocols, and HIPAA-aware handling procedures.',
    category: 'specialized',
    icon: 'heart',
    useCases: [
      'Lab specimen transport between clinics and labs',
      'Pharmaceutical delivery to pharmacies and hospitals',
      'Medical device and supply distribution',
      'Vaccine and biological material transport',
      'Patient record and confidential document delivery',
    ],
    benefits: [
      'Temperature-controlled vehicles for sensitive cargo',
      'Chain-of-custody tracking and documentation',
      'Trained drivers with healthcare handling experience',
      'Priority dispatch for medical-grade urgency',
      'Compliant packaging and transport protocols',
    ],
    whoItsFor: ['Hospitals', 'Labs', 'Pharmacies', 'Clinics'],
  },
];

export const serviceCategories = [
  { key: 'delivery', title: 'Delivery Types', description: 'Choose the right delivery speed and method for your shipment.' },
  { key: 'business', title: 'Business Solutions', description: 'Logistics solutions built for companies that ship regularly.' },
  { key: 'specialized', title: 'Specialized Services', description: 'Industry-specific courier services for critical shipments.' },
] as const;

export function getServiceBySlug(slug: string): ServiceData | undefined {
  return services.find(s => s.slug === slug);
}

export function getServicesByCategory(category: string): ServiceData[] {
  return services.filter(s => s.category === category);
}
